import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Form, message, Button, Input, InputNumber, Spin, Switch, Select } from "antd";
import { LoadingOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { RxListBullet } from "react-icons/rx";
import { createRewardType } from "../../../../api/Services";
import CustomBreadcrumb from "../../../../components/breadcrumb/Breadcrumb";
import { getRewardTypesBasePath } from "../../../../helpers/RewardsPathHelper";

import styles from "./NewRewardType.module.scss";
import {
  REWARD_VALUE_TYPE_NOMINAL,
  rewardValueTypeSelectOptions,
} from "../rewardValueTypes";

const NewRewardType = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = getRewardTypesBasePath(location.pathname);

  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const { name, status, values: valuesList } = values;
      const rows =
        valuesList?.filter(
          (x: { value?: number }) =>
            x?.value != null && !Number.isNaN(x.value)
        ) ?? [];
      const payloadValues = rows.map(
        (x: { value: number; valueType?: string }) => ({
          value: x.value,
          valueType: x.valueType ?? REWARD_VALUE_TYPE_NOMINAL,
        })
      );

      let response = await createRewardType({
        name,
        status,
        ...(payloadValues.length > 0 ? { values: payloadValues } : {}),
      });
      if (response) {
        message.success(t("Common-CreateSuccessMessage"));
        form.resetFields();
        navigate(basePath);
      } else message.error(t("Common-ErrorMessage"));

      setLoading(false);
    } catch (error) {
      message.error(t("Common-ValidationErrorMessage"));
    }
  };

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxListBullet />,
                label: t("Common-RewardTypes"),
                path: basePath
              },
              {
                label: t("Common-NewRewardType"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Button
            onClick={handleCreate}
            className={styles.saveButton}
            type="primary"
          >
            {t("Common-Add")}
          </Button>
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.rewardTypeInfoWrapper}>
          <Form
            name="newRewardType"
            layout="vertical"
            initialValues={{ remember: true }}
            form={form}
            autoComplete="off"
            className={styles.newForm}
          >
            <div className={styles.sectionTitle}>{t("Common-BasicInformation")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Name")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("Common-NameNotEntered"),
                  },
                ]}
              >
                <Input placeholder={t("Common-Name")} />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Active")}
                name="status"
              >
                <Switch defaultChecked={false} />
              </Form.Item>
            </div>
            <div className={styles.sectionTitle}>
              {t("Common-PossibleValues")}
            </div>
            <Form.List name="values">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name: fieldName, ...restField }) => (
                    <div
                      key={key}
                      className={styles.valuesRow}
                    >
                      <Form.Item
                        {...restField}
                        name={[fieldName, "valueType"]}
                        initialValue={REWARD_VALUE_TYPE_NOMINAL}
                        rules={[{ required: true, message: t("Common-RequiredField") }]}
                        className={styles.valueTypeSelect}
                      >
                        <Select
                          options={rewardValueTypeSelectOptions(t)}
                          placeholder={t("RewardValueType-ValueKind")}
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[fieldName, "value"]}
                        rules={[{ required: true, message: t("Common-Value") }]}
                        className={styles.formItem}
                      >
                        <InputNumber
                          className={styles.inputNumber}
                          min={0}
                          step={0.01}
                          placeholder={t("Common-Value")}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(fieldName)}
                        className={styles.removeValueBtn}
                      />
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() =>
                        add({ valueType: REWARD_VALUE_TYPE_NOMINAL })
                      }
                      block
                      icon={<PlusOutlined />}
                      className={styles.addValueBtn}
                    >
                      {t("Common-Add")} {t("Common-Value")}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default NewRewardType;
